'use client';

import { useEffect, useRef, useState } from 'react';
import { useLoadedWebpSource } from '@/components/tools/webp-tool-shared';
import { consumeToolUsage, formatToolCreditSuccess } from '@/lib/browser-tool-usage';
import {
  formatFileSize,
  getBaseName,
  optimizeWebp,
  type WebpOptimizationPreset,
} from '@/lib/browser-webp-tools';

type FpsCapOption = 'keep' | '30' | '20' | '12';

export default function BrowserWebpOptimizer() {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { source, loadSource, clearSource } = useLoadedWebpSource();
  const [outputUrl, setOutputUrl] = useState<string | null>(null);
  const [downloadName, setDownloadName] = useState<string | null>(null);
  const [resultSize, setResultSize] = useState<number | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);
  const [preset, setPreset] = useState<WebpOptimizationPreset>('balanced');
  const [fpsCap, setFpsCap] = useState<FpsCapOption>('keep');
  const [colorCount, setColorCount] = useState(192);

  useEffect(() => {
    return () => {
      if (outputUrl) {
        URL.revokeObjectURL(outputUrl);
      }
    };
  }, [outputUrl]);

  function resetOutputState() {
    if (outputUrl) {
      URL.revokeObjectURL(outputUrl);
    }

    setOutputUrl(null);
    setDownloadName(null);
    setResultSize(null);
  }

  function applyPreset(nextPreset: WebpOptimizationPreset) {
    setPreset(nextPreset);

    if (nextPreset === 'balanced') {
      setFpsCap('keep');
      setColorCount(192);
    } else if (nextPreset === 'smaller') {
      setFpsCap('20');
      setColorCount(128);
    } else {
      setFpsCap('12');
      setColorCount(64);
    }

    resetOutputState();
  }

  async function handleFileSelection(file: File | null) {
    resetOutputState();
    setErrorMessage(null);
    setCreditMessage(null);

    if (!file) {
      clearSource();
      return;
    }

    try {
      await loadSource(file);
    } catch (error) {
      clearSource();
      setErrorMessage(error instanceof Error ? error.message : 'The WebP could not be loaded.');
    }
  }

  async function runOptimization() {
    if (!source) {
      setErrorMessage('Choose one WebP first.');
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);
    setCreditMessage(null);

    try {
      const credits = await consumeToolUsage(
        'webp-optimizer',
        'Credits are not available for another WebP optimization right now.',
      );
      const blob = await optimizeWebp({
        file: source.file,
        preset,
        fpsCap: source.metadata.animated && fpsCap !== 'keep' ? Number(fpsCap) : null,
        colorCount,
      });
      const nextUrl = URL.createObjectURL(blob);

      resetOutputState();
      setOutputUrl(nextUrl);
      setDownloadName(`${getBaseName(source.file.name)}-optimized.webp`);
      setResultSize(blob.size);
      setCreditMessage(formatToolCreditSuccess('WebP optimization', credits));
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'The WebP could not be optimized.');
    } finally {
      setIsProcessing(false);
    }
  }

  const savings =
    source && resultSize !== null
      ? `${Math.max(0, Math.round((1 - resultSize / Math.max(source.file.size, 1)) * 100))}%`
      : null;

  return (
    <article className="tool-live-card">
      <div className="tool-live-header">
        <div>
          <h2>Optimize a still or animated WebP</h2>
          <p>
            Shrink WebP files locally with cleaner browser-side presets for quality, animation
            timing, and lighter delivery.
          </p>
        </div>
        <span className="tool-live-badge">WebP</span>
      </div>

      <div className="tool-live-routes" aria-label="Optimizer presets">
        {[
          ['balanced', 'Balanced'],
          ['smaller', 'Smaller file'],
          ['aggressive', 'Aggressive'],
        ].map(([id, label]) => (
          <button
            key={id}
            className={`tool-live-route ${preset === id ? 'tool-live-route-active' : ''}`}
            onClick={() => applyPreset(id as WebpOptimizationPreset)}
            type="button"
          >
            {label}
          </button>
        ))}
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <button
            className={`tool-live-dropzone ${isDragActive ? 'tool-live-dropzone-active' : ''}`}
            onClick={() => inputRef.current?.click()}
            onDragEnter={() => setIsDragActive(true)}
            onDragLeave={() => setIsDragActive(false)}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragActive(true);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragActive(false);
              void handleFileSelection(event.dataTransfer.files?.[0] ?? null);
            }}
            type="button"
          >
            <div>
              <strong>{source ? source.file.name : 'Choose one WebP or drop it here'}</strong>
              <span>
                {source
                  ? `${formatFileSize(source.file.size)} • ${source.metadata.width} × ${source.metadata.height}`
                  : 'Still and animated WebP files are supported'}
              </span>
            </div>
          </button>
          <input
            ref={inputRef}
            accept="image/webp,.webp"
            className="tool-live-file-input"
            onChange={(event) => void handleFileSelection(event.target.files?.[0] ?? null)}
            type="file"
          />

          {source?.metadata.animated ? (
            <div className="tool-live-controls">
              <label className="tool-live-field">
                <span>FPS cap</span>
                <select
                  onChange={(event) => {
                    setFpsCap(event.target.value as FpsCapOption);
                    resetOutputState();
                  }}
                  value={fpsCap}
                >
                  <option value="keep">Keep</option>
                  <option value="30">30 fps</option>
                  <option value="20">20 fps</option>
                  <option value="12">12 fps</option>
                </select>
              </label>
              <label className="tool-live-field">
                <span>Color count hint</span>
                <select
                  onChange={(event) => {
                    setColorCount(Number(event.target.value));
                    resetOutputState();
                  }}
                  value={colorCount}
                >
                  {[256, 192, 128, 96, 64].map((count) => (
                    <option key={count} value={count}>
                      {count}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          ) : (
            <div className="tool-live-panel-note">
              <strong>Still WebP mode</strong>
              <span>
                Still files use quality-first recompression. Animated controls stay hidden.
              </span>
            </div>
          )}

          <div className="hero-actions tool-live-actions">
            <button
              className="button"
              disabled={!source || isProcessing}
              onClick={() => void runOptimization()}
              type="button"
            >
              {isProcessing ? 'Optimizing…' : 'Optimize WebP'}
            </button>
            {outputUrl && downloadName ? (
              <a className="button button-ghost" download={downloadName} href={outputUrl}>
                Download WebP
              </a>
            ) : null}
          </div>

          {creditMessage ? (
            <p className="support-feedback support-feedback-success">{creditMessage}</p>
          ) : null}
          {errorMessage ? (
            <p className="support-feedback support-feedback-error">{errorMessage}</p>
          ) : null}
        </div>

        <aside className="tool-live-panel">
          <h3>Optimization summary</h3>
          <div className="tool-live-stats">
            <div>
              <strong>Type</strong>
              <span>
                {source
                  ? source.metadata.animated
                    ? 'Animated WebP'
                    : 'Still WebP'
                  : 'No file loaded'}
              </span>
            </div>
            <div>
              <strong>Frames</strong>
              <span>{source?.metadata.animated ? source.metadata.frameCount : '1'}</span>
            </div>
            <div>
              <strong>Duration</strong>
              <span>
                {source?.metadata.animated
                  ? `${(source.metadata.durationMs / 1000).toFixed(2)}s`
                  : 'Static'}
              </span>
            </div>
            <div>
              <strong>Output size</strong>
              <span>{resultSize ? formatFileSize(resultSize) : 'Not exported yet'}</span>
            </div>
            <div>
              <strong>Savings</strong>
              <span>{savings ?? 'Not available yet'}</span>
            </div>
          </div>
        </aside>
      </div>

      <div className="tool-live-preview">
        <h3>Preview</h3>
        <div className="tool-image-compare-grid">
          <div className="tool-image-compare-card">
            <strong>Original</strong>
            <div className="tool-live-preview-frame">
              {source ? (
                <img alt="Original WebP preview" src={source.previewUrl} />
              ) : (
                <div className="tool-editor-empty">
                  <strong>Original preview</strong>
                  <span>Upload one WebP to inspect and optimize it here.</span>
                </div>
              )}
            </div>
          </div>
          <div className="tool-image-compare-card">
            <strong>Optimized</strong>
            <div className="tool-live-preview-frame">
              {outputUrl ? (
                <img alt="Optimized WebP preview" src={outputUrl} />
              ) : (
                <div className="tool-editor-empty">
                  <strong>Optimized preview</strong>
                  <span>Run the optimizer to preview the lighter WebP export.</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}
